import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { GameProvider, useGame } from "@/context/GameContext";
import Nav from "@/components/Nav";
import WelcomeBonus from "@/components/WelcomeBonus";
import MwpTierAlert from "@/components/MwpTierAlert";
import GiftPopup from "@/components/GiftPopup";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import CreateWolf from "@/pages/CreateWolf";
import Journey from "@/pages/Journey";
import Battle from "@/pages/Battle";
import Profile from "@/pages/Profile";
import Wallet from "@/pages/Wallet";
import Store from "@/pages/Store";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Admin from "@/pages/Admin";
import Leaderboard from "@/pages/Leaderboard";
import Showcase from "@/pages/Showcase";
import Gallery from "@/pages/Gallery";
import Contact from "@/pages/Contact";
import WolfPage from "@/pages/WolfPage";
import GetNfts from "@/pages/GetNfts";
import { Footer } from "@/components/SupportEmail";

function Loading() {
  return <div data-testid="app-loading" className="min-h-screen grid place-items-center font-display text-slate-400 tracking-[0.3em]">TRACKING SCENT…</div>;
}

function Protected({ children, needWolf = true, admin = false }) {
  const { user, game } = useGame();
  if (user === null || (user && !game)) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/journey" replace />;
  if (needWolf && !game.wolf && !admin) return <Navigate to="/create" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
      <Nav />
      <GiftPopup />
      <WelcomeBonus />
      <MwpTierAlert />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/showcase" element={<Showcase />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/nfts" element={<GetNfts />} />
        <Route path="/wolf/:name" element={<WolfPage />} />
        <Route path="/create" element={<Protected needWolf={false}><CreateWolf /></Protected>} />
        <Route path="/journey" element={<Protected><Journey /></Protected>} />
        <Route path="/battle/:id" element={<Protected><Battle /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/wallet" element={<Protected needWolf={false}><Wallet /></Protected>} />
        <Route path="/store" element={<Protected needWolf={false}><Store /></Protected>} />
        <Route path="/payment/success" element={<Protected needWolf={false}><PaymentSuccess /></Protected>} />
        <Route path="/payment/cancel" element={<Protected needWolf={false}><PaymentCancel /></Protected>} />
        <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!location.pathname.startsWith("/battle") && <Footer />}
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <GameProvider>
          <AppRouter />
          <Toaster theme="dark" position="top-center" richColors />
        </GameProvider>
      </BrowserRouter>
    </div>
  );
}
